<template>
  <div ref="root" class="rd rd--firms" :class="{ 'ui-compact': compact }" :data-w="layout" :data-density="density" :data-bar="barSticky ? 'sticky' : 'flow'" :style="rootStyle">
    <div class="rd-main">
      <div class="rd-page">
        <div class="rd-head">
          <div class="rd-head__title">
            <h1>{{ $t("companies_delivery") }}</h1>
            <p class="rd-head__sub">{{ $t("manage_delivery_companies") }}</p>
          </div>
          <div class="rd-head__right">
            <button type="button" class="ui-icon-btn ui-icon-btn--sm" :title="`${$t('refresh')} (r)`" :aria-label="$t('refresh')" @click="reload">
              <v-icon size="18">mdi-refresh</v-icon>
            </button>
            <button type="button" class="ui-icon-btn ui-icon-btn--sm ui-icon-btn--plain" :title="`${$t('report_desktop.shortcuts')} (?)`" :aria-label="$t('report_desktop.shortcuts')" :aria-expanded="String(helpOpen)" @click="helpOpen = !helpOpen">
              <v-icon size="18">mdi-keyboard-outline</v-icon>
            </button>
            <button type="button" class="ui-icon-btn ui-icon-btn--sm" :title="$t('print')" :aria-label="$t('print')" @click="printPage">
              <v-icon size="18">mdi-printer-outline</v-icon>
            </button>
            <ui-button v-if="canCreate" variant="primary" icon="mdi-plus" aria-keyshortcuts="n" @click="openForm(0)">{{ compact ? $t("delivery_firms.new_short") : $t("delivery_firms.new") }}</ui-button>
            <button type="button" class="ui-text-btn rd-nav__legacy" :title="$t('report_desktop.old_view')" @click="$emit('legacy')">
              <v-icon size="16" color="#c2410c">mdi-history</v-icon>
              <span class="rd-nav__legacy-label">{{ $t("report_desktop.old_view") }}</span>
            </button>
          </div>
        </div>
      </div>

      <ui-filter-bar v-if="status === 'ready' && firms.length" ref="bar" :segments="segments" :query="query" :placeholder="$t('search_delivery_companies_placeholder')" :search-label="$t('search')" :count="$t('delivery_firms.count', { shown, total: counts.all })" @segment="(id, value) => setStatus(value)" @query="query = $event" />

      <div class="rd-page ui-screen">
        <ui-state v-if="status === 'loading'" kind="loading" />
        <ui-state v-else-if="status === 'error'" kind="error" icon="mdi-alert-outline" :title="$t('delivery_firms.state.error_title')" :text="$t('delivery_firms.state.error_text')">
          <ui-button variant="dark" icon="mdi-refresh" @click="reload">{{ $t("delivery_firms.state.retry") }}</ui-button>
        </ui-state>
        <ui-state v-else-if="!firms.length" kind="empty" icon="mdi-truck-delivery-outline" :title="$t('delivery_firms.state.empty_title')" :text="$t('delivery_firms.state.empty_text')">
          <ui-button v-if="canCreate" variant="primary" icon="mdi-plus" @click="openForm(0)">{{ $t("delivery_firms.new") }}</ui-button>
        </ui-state>
        <template v-else>
          <ui-kpis :tiles="kpiTiles" :label="$t('delivery_firms.kpi.label')" @pick="pickKpi" />
          <ui-state v-if="!rows.length" kind="empty" icon="mdi-magnify" :title="$t('delivery_firms.state.filtered_title')" :text="$t('delivery_firms.state.filtered_text')">
            <ui-button @click="resetFilters">{{ $t("delivery_firms.state.reset") }}</ui-button>
          </ui-state>
          <firm-list v-else :rows="rows" :active-id="activeId" @open="(firm) => openFirm(firm.id)" @toggle="toggleBranches" @toggle-orders="toggleOrders" @cooperation="openCooperationFor" />
        </template>
      </div>
    </div>

    <report-detail-panel :stack="panelStack" @close="closePanel" @back="panelBack" @open="openPanel" @toast="onPanelToast" />
    <transition name="rd-fade">
      <div v-if="panelStack.length" class="rd-scrim" @click="closePanel"></div>
    </transition>

    <report-help-popover :open="helpOpen" :shortcuts="shortcuts" @close="helpOpen = false" />
    <ui-toast :toast="toast" @action="runToastAction" />

    <restaurant-settings-dialog v-if="adminFirm" v-model="adminDialog" :firm="adminFirm" :countries="countries" :cities="adminCities" :edited-item="adminEdited" @country-change="onAdminCountryChange" @saved="onAdminSaved" @close="adminDialog = false" />
  </div>
</template>

<script>
import "@/components/report/desktop/reportDesktop.scss";
import "./firmsShell.scss";
import ReportHelpPopover from "@/components/report/shared/ReportHelpPopover.vue";
import ReportDetailPanel from "@/components/report/desktop/ReportDetailPanel.vue";
import hasReportShell from "@/components/report/shared/hasReportShell";
import hasPrinting from "@/mixins/hasPrinting";
import { escapeHtml } from "@/utils/escapeHtml";
import RestaurantSettingsDialog from "@/components/companies/dialogs/RestaurantSettingsDialog.vue";
import companyService from "@/services/company";
import restaurantService from "@/services/restaurant";
import postService from "@/services/post";
import hasFirmLabels from "./hasFirmLabels";
import FirmList from "./parts/FirmList.vue";
import FirmPanel from "./panels/FirmPanel.vue";
import FirmFormPanel from "./panels/FirmFormPanel.vue";
import CooperationPanel from "./panels/CooperationPanel.vue";
import { FIRMS_QUERY, STATUS_VALUES } from "./firmsQuery";
import {
  buildFirmBody,
  buildLinkBody,
  buildRows,
  buildStatusBody,
  countFirms,
  flattenFirms,
  mapFirms,
  mapServerErrors,
} from "./firmsLogic";

const COMPACT_WIDTH = 760;

/**
 * Firme za dostavu (/firmeDostava). Isti izgled kao izvještaji, magacin, recepti, finansije i korisnici dostave: zaglavlje,
 * brojke koje filtriraju, traka filtera, lista (tabela ili kartice) i detalji, izmjena i saradnja u desnom panelu.
 * Podružnice su ispod matične firme. Status se bira u adresi (?s=); ?ui=stari vraća staru stranicu.
 * Panele čitaju firme iz ove ljuske (inject reportShell), pa se kartica sama osvježi kad se lista ponovo učita.
 * Admin i dalje povezuje restorane u starom punoekranskom dijalogu (tabela svih restorana grada).
 */
export default {
  name: "DeliveryFirmsShell",
  mixins: [hasReportShell, hasFirmLabels, hasPrinting],
  components: { ReportHelpPopover, ReportDetailPanel, FirmList, RestaurantSettingsDialog },
  data: () => ({
    status: "loading",
    firms: [],
    countries: [],
    allCities: [],
    query: "",
    open: {},
    adminFirm: null,
    adminDialog: false,
    adminEdited: { country: null, city: null },
    adminCities: [],
  }),
  computed: {
    phone() {
      return Boolean(this.device && this.device.isMobile);
    },
    /** kartice umjesto tabele i na tabletu: ispod 760 px širine stranice tabela ne stane sa prekidačem */
    compact() {
      return this.phone || this.width < COMPACT_WIDTH;
    },
    isAdmin() {
      return this.userType === "admin";
    },
    /** nova firma: admin i restoran, i to tek kad je restoran izabran (kao na staroj stranici) */
    canCreate() {
      return (
        ["admin", "restaurant"].includes(this.userType) &&
        Boolean(this.$store.state.restaurant)
      );
    },
    flat() {
      return flattenFirms(this.firms);
    },
    filterStatus() {
      const wanted = this.$route.query[FIRMS_QUERY.status];
      return STATUS_VALUES.includes(wanted) ? wanted : "all";
    },
    counts() {
      return countFirms(this.firms, this.restaurantId);
    },
    /** segment statusa u traci filtera; "Bez naplate" se vidi samo kad ima takvih firmi ili je već izabran */
    segments() {
      const options = [
        { value: "all", label: this.$t("delivery_firms.status.all") },
        { value: "active", label: this.$t("delivery_firms.status.active") },
        { value: "inactive", label: this.$t("delivery_firms.status.inactive") },
      ];
      if (this.counts.billing > 0 || this.filterStatus === "billing") {
        options.push({ value: "billing", label: this.$t("delivery_firms.status.billing") });
      }
      return [{ id: "status", label: this.$t("status"), value: this.filterStatus, options }];
    },
    kpiTiles() {
      const { counts, filterStatus } = this;
      return [
        { key: "all", label: this.$t("delivery_firms.kpi.all"), value: counts.all, on: filterStatus === "all" },
        { key: "active", label: this.$t("delivery_firms.kpi.active"), value: counts.active, on: filterStatus === "active" },
        { key: "inactive", label: this.$t("delivery_firms.kpi.inactive"), value: counts.inactive, on: filterStatus === "inactive" },
        { key: "billing", label: this.$t("delivery_firms.kpi.billing"), value: counts.billing, on: filterStatus === "billing", warn: counts.billing > 0 },
      ];
    },
    rows() {
      return buildRows(
        this.firms,
        {
          status: this.filterStatus,
          query: this.query,
          open: this.open,
          restaurantId: this.restaurantId,
        },
        this.toLatin,
        this.cityName
      );
    },
    shown() {
      return this.rows.filter((row) => !row.context).length;
    },
    activeId() {
      const top = this.panelStack[this.panelStack.length - 1];
      return top && top.props ? top.props.firmId || 0 : 0;
    },
    menuKeys() {
      return [];
    },
    shortcuts() {
      return [
        { keys: "n", label: this.$t("delivery_firms.keys.new") },
        { keys: "/", label: this.$t("delivery_firms.keys.search") },
        { keys: "r", label: this.$t("delivery_firms.keys.refresh") },
        { keys: "Esc", label: this.$t("report_desktop.keys_close") },
        { keys: "?", label: this.$t("report_desktop.shortcuts") },
      ];
    },
  },
  watch: {
    // ponovno učitavanje radi događaj changeWebSite (kao na staroj stranici); ovdje se samo zatvara panel starog restorana
    restaurantId() {
      this.closePanel();
    },
  },
  created() {
    this.loadFirms();
    this.loadReference();
    this.$root.$on("changeWebSite", this.reload);
  },
  beforeDestroy() {
    this.$root.$off("changeWebSite", this.reload);
  },
  methods: {
    // hasReportShell zove ove kad korisnik pritisne r ili [ ]
    reload() {
      this.loadFirms({ silent: this.status === "ready" });
    },
    shiftDay() {
      // lista nema dane
    },
    openScreen() {
      // jedan ekran, bez menija
    },
    // ------------------------------------------------------------ podaci
    async loadFirms({ silent = false } = {}) {
      if (!silent) this.status = "loading";
      try {
        const response = await companyService.getDeliveryCompanies();
        this.firms = mapFirms(response.data);
        this.status = "ready";
      } catch (error) {
        if (!silent) {
          this.status = "error";
        } else {
          this.showToast(this.$t("error_loading_delivery_companies"));
        }
      }
    },
    /** države (za izbor države i grada u formi) i gradovi (za naziv grada u listi); bez njih lista i dalje radi */
    async loadReference() {
      const [countries, cities] = await Promise.allSettled([
        restaurantService.getCountriesAll(),
        postService.getAllCities(),
      ]);
      if (countries.status === "fulfilled") this.countries = countries.value.data || [];
      else this.showToast(this.$t("error_loading_countries"));
      if (cities.status === "fulfilled") this.allCities = cities.value.data || [];
      else this.showToast(this.$t("error_loading_cities"));
    },
    cityName(id) {
      if (!id) return "";
      const city = this.allCities.find((item) => item.id == id);
      return city ? city.name : "";
    },
    /** id države čiji spisak gradova sadrži grad (država nije kolona firme, 12.09.2026 B2) */
    countryOfCity(cityId) {
      const country = this.countries.find((item) =>
        (item.city || []).some((city) => city.id == cityId)
      );
      return country ? country.id : "";
    },
    usersRoute(firm) {
      return {
        name: "firma-dostava-id-korisnici",
        params: { id: firm.id, name: firm.name, parent_id: firm.parentId },
      };
    },
    // ------------------------------------------------------------ filteri
    setQuery(patch) {
      const query = { ...this.$route.query, ...patch };
      Object.keys(query).forEach(
        (key) => (query[key] === undefined || query[key] === "all") && delete query[key]
      );
      this.$router.replace({ query }).catch(() => {});
    },
    setStatus(status) {
      this.setQuery({ [FIRMS_QUERY.status]: status === "all" ? undefined : status });
    },
    pickKpi(key) {
      this.query = "";
      this.setStatus(key);
    },
    resetFilters() {
      this.query = "";
      this.setStatus("all");
    },
    toggleBranches(firm) {
      this.$set(this.open, firm.id, this.open[firm.id] === false);
    },
    // ------------------------------------------------------------ paneli
    openFirm(id) {
      const firm = this.flat.find((item) => item.id === id);
      if (!firm) return;
      this.openPanel({
        key: `firm-${id}`,
        title: this.firmName(firm),
        subtitle: `#${id}`,
        component: FirmPanel,
        props: { firmId: id },
        root: true,
      });
    },
    openForm(id = 0, parentId = 0) {
      const firm = id ? this.flat.find((item) => item.id === id) : null;
      if (id && !firm) return;
      const parent = parentId ? this.flat.find((item) => item.id === parentId) : null;
      this.openPanel({
        key: `firm-form-${id}-${parentId}`,
        title: id
          ? this.$t("edit_delivery_company")
          : parent
          ? this.$t("delivery_firms.panel.add_branch")
          : this.$t("add_delivery_company"),
        subtitle: firm
          ? this.firmName(firm)
          : parent
          ? this.firmName(parent)
          : this.$t("delivery_firms.form.required_note"),
        component: FirmFormPanel,
        props: { firmId: id, parentId },
        root: true,
      });
    },
    cancelForm(id) {
      if (id) this.openFirm(id);
      else this.closePanel();
    },
    /** zupčanik u redu i na kartici firme: admin otvara dijalog "Pridruži restoran", restoran panel sa svojom saradnjom */
    openCooperationFor(firm) {
      if (this.isAdmin) this.openAdminLinks(firm);
      else this.openCooperation(firm.id);
    },
    openCooperation(id) {
      const firm = this.flat.find((item) => item.id === id);
      if (!firm) return;
      this.openPanel({
        key: `firm-coop-${id}`,
        title: this.$t("tooltip_delivery_settings"),
        subtitle: this.firmName(firm),
        component: CooperationPanel,
        props: { firmId: id },
        root: true,
      });
    },
    onPanelToast(text, options) {
      this.showToast(text, options || {});
    },
    /** Štampa spiska firmi kao u izvještajima: poseban prozor samo sa tabelom (redovi kakvi su na ekranu, sa filterima). */
    printPage() {
      const t = (key) => this.$t(key);
      const yes = (on) => this.$t(on ? "yes" : "no");
      const dash = "—";
      const columns = [
        { text: t("delivery_firms.col.firm"), value: "firm" },
        { text: t("status"), value: "status" },
        { text: t("delivery_firms.col.coop"), value: "coop" },
        { text: t("city"), value: "city" },
        { text: t("delivery_firms.col.contact"), value: "contact" },
        { text: t("users"), value: "users", align: "right" },
        { text: t("delivery_firms.col.orders"), value: "orders" },
      ].map((column) => ({ ...column, text: escapeHtml(column.text) }));
      const items = this.rows.map((row) => {
        const firm = row.firm;
        const link = this.linkOf(firm);
        let coop = dash;
        if (link) {
          const parts = [t(link.internal ? "delivery_firms.coop.internal" : "delivery_firms.coop.external")];
          if (this.isBillingMissing(firm)) {
            parts.push(t("delivery_firms.coop.unpaid"));
          } else if (link.payType) {
            parts.push(`${this.payAmountText(link.payType, link.paying, this.firmCurrency(firm))} ${this.payTypeLabel(link.payType).toLowerCase()}`);
          }
          coop = parts.join(" · ");
        } else if (this.userType === "admin" && firm.links.length) {
          coop = this.$t("delivery_firms.coop.restaurants", { count: firm.links.length });
        }
        const name = `${row.depth ? "↳ " : ""}${this.firmName(firm)} (#${firm.id})`;
        const cells = {
          firm: name,
          status: this.stateText(firm),
          coop,
          city: this.cityLabel(firm) || dash,
          contact: [firm.email, firm.phone].filter(Boolean).join(", ") || dash,
          users: this.can(firm).users ? firm.usersCount : dash,
          orders: this.can(firm).toggle ? yes(link && link.activeCompany) : dash,
        };
        return Object.fromEntries(Object.entries(cells).map(([key, value]) => [key, escapeHtml(value)]));
      });
      const segment = this.segments[0].options.find((option) => option.value === this.filterStatus);
      const subtitle = [
        this.toLatin((this.$store.state.restaurant && this.$store.state.restaurant.name) || ""),
        this.filterStatus !== "all" && segment ? segment.label : "",
        this.query.trim() ? `"${this.query.trim()}"` : "",
      ].filter(Boolean).join(" · ");
      this.printDocument({
        pageTitle: t("companies_delivery"),
        title: escapeHtml(t("companies_delivery")),
        subtitle: escapeHtml(subtitle),
        tables: [{ headers: columns, items }],
      });
    },
    // ------------------------------------------------------------ čuvanje (zovu ga forme u panelima)
    /** Vraća { fields, rest } kad backend odbije podatke (forma ih prikazuje uz polja), a prazno kad je sve uspjelo. */
    async submitFirm(form, original) {
      const isNew = !form.id;
      const body = buildFirmBody(form, { isAdmin: this.isAdmin, original });
      try {
        let id = form.id;
        if (isNew) {
          const response = await companyService.createDeliveryCompany(body);
          id =
            response && response.data && response.data.id ? Number(response.data.id) : 0;
        } else {
          await companyService.updateDeliveryCompany(form.id, body);
        }
        await this.loadFirms({ silent: true });
        this.showToast(
          this.$t(isNew ? "delivery_company_created" : "delivery_company_updated")
        );
        if (id && this.flat.some((item) => item.id === id)) this.openFirm(id);
        else this.closePanel();
        return {};
      } catch (error) {
        const response = error.response;
        if (response && response.status === 422) {
          return mapServerErrors(response);
        }
        return { fields: {}, rest: this.$t("error_saving_delivery_company") };
      }
    },
    /** Saradnja izabranog restorana: jedan red i id firme (BUG 8 i 17, 12.09.2026). */
    async submitCooperation(form, firm) {
      const body = buildLinkBody(form, {
        firmId: firm.id,
        restaurantId: this.restaurantId,
        original: this.linkOf(firm),
      });
      try {
        await companyService.storeDeliveryCompanyRestaurants(body);
        await this.loadFirms({ silent: true });
        this.showToast(this.$t("firm_settings_saved"));
        this.openFirm(firm.id);
        return {};
      } catch (error) {
        const data = error.response && error.response.data;
        if (data && data.status === "failed" && data.message) {
          return { fields: {}, rest: this.toLatin(data.message) };
        }
        return { fields: {}, rest: this.$t("error_saving_firm_settings") };
      }
    },
    /** Prekidač "Prima narudžbe": odmah se vidi, a pri grešci se vraća na staro. */
    async toggleOrders(firm, on) {
      const link = this.linkOf(firm);
      if (!link || link.activeCompany === on) return;
      const before = link.activeCompany;
      link.activeCompany = on;
      try {
        await companyService.changeCompanyDeliveryStatus(buildStatusBody(link, on));
        this.showToast(this.$t("company_delivery_status_updated"));
      } catch (error) {
        link.activeCompany = before;
        this.showToast(this.$t("error_updating_company_delivery_status"));
      }
    },
    // ------------------------------------------------------------ admin: povezivanje restorana (stari dijalog)
    openAdminLinks(firm) {
      const match = firm.cityId ? this.findCountryAndCity(firm.cityId) : null;
      this.adminFirm = firm.raw;
      this.adminEdited = {
        country: match ? match.country : null,
        city: match ? match.city : null,
      };
      this.adminCities = match ? match.country.city || [] : [];
      this.adminDialog = true;
    },
    findCountryAndCity(cityId) {
      for (const country of this.countries) {
        const city = (country.city || []).find((item) => item.id == cityId);
        if (city) return { country, city };
      }
      return null;
    },
    onAdminCountryChange() {
      const country = this.adminEdited.country;
      this.adminCities = country ? country.city || [] : [];
    },
    onAdminSaved() {
      this.adminDialog = false;
      this.loadFirms({ silent: true });
    },
    onKeydown(event) {
      const target = event.target;
      const typing =
        target &&
        (["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName) ||
          target.isContentEditable);
      if (
        event.key === "n" &&
        this.canCreate &&
        !typing &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !document.querySelector(".v-dialog--active")
      ) {
        event.preventDefault();
        this.openForm(0);
        return;
      }
      hasReportShell.methods.onKeydown.call(this, event);
    },
  },
};
</script>
